import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchData, fetchAllData } from "../../helpers/externapi";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { downloadCSVData, downloadExcelData, formatDate } from '../../Commoncomponents/CommonComponents';
import CommonTables from '../../Commoncomponents/CommonTables';
import moment from 'moment';
import Modal from 'react-modal';
import { TableSkeletonLoading } from "../../Commoncomponents/SkeletonLoading";

export default function BankDetailsList(props) {
    const [loading, setLoading] = useState(false);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [error, setError] = useState('');
    const [allData, setAllData] = useState([]);
    const [membersData, setMembersData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    // searchInput = what the user types; committedSearch = what was last searched
    const [searchInput, setSearchInput] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);

    // ─── Filter state ─────────────────────────────────────────────────────────────
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [pendingFilterType, setPendingFilterType] = useState(null);
    const [pendingFromDate, setPendingFromDate] = useState('');
    const [pendingToDate, setPendingToDate] = useState('');
    const [appliedFilterType, setAppliedFilterType] = useState(null);
    const [appliedFromDate, setAppliedFromDate] = useState('');
    const [appliedToDate, setAppliedToDate] = useState('');
    const [filteredCount, setFilteredCount] = useState(null);
    const filterPanelRef = useRef(null);

    const thresholdDays = 5;
    let UserId = localStorage.getItem("UserId");
    const navigate = useNavigate();

    const FILTER_OPTIONS = [
        { label: "Today", value: "Today" },
        { label: "Yesterday", value: "Yesterday" },
        { label: "Weekly", value: "Weekly" },
        { label: "Monthly", value: "Monthly" },
        { label: "Custom Range", value: "Custom" },
    ];

    const tableHeads = [
        "NAME", "MOBILE NUMBER", "REGISTER ON", "BANK NAME",
        "ACCOUNT NUMBER", "IFSC CODE", "BRANCH", "CITY", "UPI ID",
        "TOTAL SALES", "TODAY SALES", "YESTERDAY SALES", "MONTHLY SALES",
        "VERIFIED RESPONSE", "VERIFIED ON", "REPORTING TO", "REPORTING TO DESIGNATION"
    ];

    // ─── POST helper ──────────────────────────────────────────────────────────────
    const fetchBankDetailsPost = async (filterType = null, fromDate = null, toDate = null) => {
        try {
            const body = { filterType };
            if (filterType === "Custom" && fromDate && toDate) {
                body.fromDate = fromDate;
                body.toDate = toDate;
            }
            const response = await fetchData(
                "lambdaAPI/BankDetails/GetEmployeesWithBankDetails",
                body
            );
            return Array.isArray(response) ? response : [];
        } catch (err) {
            console.error("Error fetching bank details:", err);
            return [];
        }
    };

    // ─── Initial load ─────────────────────────────────────────────────────────────
    const loadInitialData = async () => {
        setLoading(true);
        const data = await fetchBankDetailsPost(null);
        setAllData(data);
        setFilteredData(data);
        setTotalCount(data.length);
        setFilteredCount(null);
        setIsDataLoaded(true);
        setLoading(false);
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    // ─── Search triggered only on button click / Enter ────────────────────────────
    // Runs when committedSearch changes (set by handleSearch / handleClearSearch)
    useEffect(() => {
        if (!isDataLoaded) return;

        const query = committedSearch.trim().toLowerCase();
        if (!query) {
            setFilteredData(allData);
            setTotalCount(allData.length);
        } else {
            const filtered = allData.filter(item => {
                const nameMatch = item.name?.toLowerCase().includes(query);
                const mobileMatch = item.mobileNumber?.includes(query);
                return nameMatch || mobileMatch;
            });
            setFilteredData(filtered);
            setTotalCount(filtered.length);
        }
        setCurrentPage(1);
    }, [committedSearch, allData, isDataLoaded]);

    // ─── Paginate with TableSkeletonLoading ───────────────────────────────────────
    useEffect(() => {
        if (!isDataLoaded) return;

        setPaginationLoading(true);
        const timer = setTimeout(() => {
            const skip = (currentPage - 1) * perPage;
            const slice = filteredData.slice(skip, skip + perPage);
            setMembersData(slice);
            setPaginationLoading(false);
        }, 300);

        return () => clearTimeout(timer);
    }, [filteredData, currentPage, perPage, isDataLoaded]);

    // ─── Close filter panel on outside click ─────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
                setShowFilterPanel(false);
            }
        };
        if (showFilterPanel) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showFilterPanel]);

    // ─── Filter Apply ─────────────────────────────────────────────────────────────
    const handleApplyFilter = async () => {
        if (pendingFilterType === "Custom" && (!pendingFromDate || !pendingToDate)) return;

        setLoading(true);
        const data = await fetchBankDetailsPost(
            pendingFilterType,
            pendingFilterType === "Custom" ? pendingFromDate : null,
            pendingFilterType === "Custom" ? pendingToDate : null
        );
        setLoading(false);

        setAllData(data);
        setFilteredData(data);
        setTotalCount(data.length);
        setFilteredCount(data.length);
        setAppliedFilterType(pendingFilterType);
        setAppliedFromDate(pendingFilterType === "Custom" ? pendingFromDate : '');
        setAppliedToDate(pendingFilterType === "Custom" ? pendingToDate : '');
        // Reset search when filter changes
        setSearchInput('');
        setCommittedSearch('');
        setCurrentPage(1);
        setShowFilterPanel(false);
    };

    // ─── Filter Clear ─────────────────────────────────────────────────────────────
    const handleClearFilter = async () => {
        setPendingFilterType(null);
        setPendingFromDate('');
        setPendingToDate('');
        setAppliedFilterType(null);
        setAppliedFromDate('');
        setAppliedToDate('');
        setFilteredCount(null);
        setSearchInput('');
        setCommittedSearch('');
        setCurrentPage(1);
        setShowFilterPanel(false);

        setLoading(true);
        const data = await fetchBankDetailsPost(null);
        setLoading(false);

        setAllData(data);
        setFilteredData(data);
        setTotalCount(data.length);
    };

    // ─── Search handlers ──────────────────────────────────────────────────────────
    // Commit the search — triggers the search useEffect
    const handleSearch = () => {
        setCommittedSearch(searchInput);
    };

    // Also trigger search on Enter key
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear both the input and the committed search
    const handleClearSearch = () => {
        setSearchInput('');
        setCommittedSearch('');
    };

    // ─── Misc handlers ────────────────────────────────────────────────────────────
    const handleSnackbarClose = () => setSnackbarOpen(false);

    const handlePageChange = (event, page) => {
        setPaginationLoading(true);
        setCurrentPage(page);
    };

    const handlePerPageChange = (event) => {
        setPaginationLoading(true);
        setPerPage(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };

    const getFilterDateDisplay = () => {
        if (!appliedFilterType) return null;

        const today = moment();

        switch (appliedFilterType) {
            case "Today":
                return today.format("DD MMM YYYY");

            case "Yesterday":
                return today.clone().subtract(1, "day").format("DD MMM YYYY");

            case "Weekly":
                return `${today.clone().startOf("week").format("DD MMM")} - ${today.clone().endOf("week").format("DD MMM YYYY")}`;

            case "Monthly":
                return `${today.clone().startOf("month").format("DD MMM")} - ${today.clone().endOf("month").format("DD MMM YYYY")}`;

            case "Custom":
                return `${moment(appliedFromDate).format("DD MMM YYYY")} - ${moment(appliedToDate).format("DD MMM YYYY")}`;

            default:
                return null;
        }
    };

    // ─── Download helpers — build filter-aware POST body ─────────────────────────
    const buildFilterBody = () => {
        const body = { filterType: appliedFilterType };
        if (appliedFilterType === "Custom" && appliedFromDate && appliedToDate) {
            body.fromDate = appliedFromDate;
            body.toDate = appliedToDate;
        }
        return body;
    };

    const handleExcelDownload = async () => {
        setLoading(true);
        try {
            const filterBody = buildFilterBody();
            await downloadExcelData(
                'bankdetailslist',
                totalCount,
                perPage,
                currentPage,
                fetchData,
                filterBody,
                setLoading
            );
        } catch (err) {
            console.error("Excel download error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCSVDownload = async () => {
        setLoading(true);
        try {
            const filterBody = buildFilterBody();
            await downloadCSVData(
                'bankdetailslist',
                totalCount,
                perPage,
                currentPage,
                fetchData,
                filterBody,
                setLoading
            );
        } catch (err) {
            console.error("CSV download error:", err);
        } finally {
            setLoading(false);
        }
    };

    // ─── Filter label helper ──────────────────────────────────────────────────────
    const getAppliedFilterLabel = () => {
        if (!appliedFilterType) return null;
        if (appliedFilterType === "Custom") return `Custom: ${appliedFromDate} ~ ${appliedToDate}`;
        return appliedFilterType;
    };

    // ─── Table rows ───────────────────────────────────────────────────────────────
    const tableElements = membersData && membersData.length > 0
        ? membersData.map((data) => ([
            <Link
                to={`/advisor/details/${data.employeeId}`}
                className="text-start-important"
                style={{ whiteSpace: 'nowrap', textDecoration: 'none', display: 'block' }}
            >
                {data.name}
            </Link>,
            <span>{data.mobileNumber}</span>,
            <span>{data.createdDate ? formatDate(data.createdDate).slice(0, 11) : ""}</span>,
            <span>{data.bankName || "N/A"}</span>,
            <span style={{ fontFamily: 'monospace' }}>{data.bankAccNumber || "N/A"}</span>,
            <span style={{ fontFamily: 'monospace' }}>{data.ifscCode || "N/A"}</span>,
            <span>{data.branch || "N/A"}</span>,
            <span>{data.city || "N/A"}</span>,
            <div className="text-center">
                {data.upiId && data.upiId !== "NULL"
                    ? <span>{data.upiId}</span>
                    : <span style={{ color: 'red' }}>N/A</span>}
            </div>,
            <span>{data.totalSales || "N/A"}</span>,
            <span>{data.todaySales || "N/A"}</span>,
            <span>{data.yesterdaySales || "N/A"}</span>,
            <span>{data.monthlySales || "N/A"}</span>,
            <div className="text-center">
                {data.verifiedResponse === "VALID"
                    ? <span className="badge bg-label-success">{data.verifiedResponse}</span>
                    : <span className="badge bg-label-danger">{data.verifiedResponse || "N/A"}</span>}
            </div>,
            <span>{data.verifiedOn ? formatDate(data.verifiedOn).slice(0, 11) : "N/A"}</span>,
            <span>{data.reportingToName ? data.reportingToName : "N/A"}</span>,
            <span>{data.reportingToRole ? data.reportingToRole : "N/A"}</span>,
        ]))
        : [];

    // ─── Filter Panel ─────────────────────────────────────────────────────────────
    const filterPanel = () => (
        <div
            ref={filterPanelRef}
            style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                zIndex: 999,
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                padding: '16px',
                minWidth: '280px',
                marginTop: '4px',
            }}
        >
            <h6 className="mb-3" style={{ fontWeight: 600 }}>Filter by Date</h6>

            <div className="d-flex flex-wrap gap-2 mb-3">
                {FILTER_OPTIONS.map(opt => (
                    <button
                        key={opt.value}
                        className={`btn btn-sm ${pendingFilterType === opt.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setPendingFilterType(opt.value)}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            {pendingFilterType === "Custom" && (
                <div className="mb-3">
                    <div className="mb-2">
                        <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 500 }}>From Date</label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={pendingFromDate}
                            onChange={e => setPendingFromDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 500 }}>To Date</label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={pendingToDate}
                            min={pendingFromDate}
                            onChange={e => setPendingToDate(e.target.value)}
                        />
                    </div>
                </div>
            )}

            <div className="d-flex gap-2 justify-content-end mt-2">
                <button className="btn btn-sm btn-outline-danger" onClick={handleClearFilter}>
                    <i className="bx bx-x me-1"></i>Clear
                </button>
                <button
                    className="btn btn-sm btn-primary"
                    disabled={
                        !pendingFilterType ||
                        (pendingFilterType === "Custom" && (!pendingFromDate || !pendingToDate))
                    }
                    onClick={handleApplyFilter}
                >
                    <i className="bx bx-check me-1"></i>Apply
                </button>
            </div>
        </div>
    );

    // ─── Toolbar UI ───────────────────────────────────────────────────────────────
    const filterUI = () => (
        <div className="card p-1 my-1 sticky-top" style={{ zIndex: 1 }}>
            <div className="row gy-2 align-items-center">

                {/* Left: Add New */}
                <div className="col-4 col-md-3">
                    <ul className="nav nav-md nav-pills">
                        <li className="nav-item">
                            <Link className="nav-link" to="/advisor/new">
                                <i className="tf-icons navbar-icon bx bx-plus-circle"></i>&nbsp;Add New
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* Middle: Search input + Search button + Clear */}
                <div className="col-12 col-md-4">
                    <label htmlFor="search-input" className="form-label">Name or Mobile Number</label>
                    <div className="d-flex align-items-center gap-2" style={{ maxWidth: '420px' }}>
                        {/* Input with inline clear icon */}
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type="text"
                                id="search-input"
                                className="form-control"
                                maxLength="50"
                                value={searchInput}
                                placeholder="Search by name or mobile..."
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                            />
                            {/* Show clear (×) only when something is committed and shown */}
                            {committedSearch && (
                                <i
                                    className="fas fa-times-circle"
                                    title="Clear search"
                                    style={{
                                        position: 'absolute', right: '10px', top: '50%',
                                        transform: 'translateY(-50%)', fontSize: '15px',
                                        color: 'red', cursor: 'pointer'
                                    }}
                                    onClick={handleClearSearch}
                                />
                            )}
                        </div>

                        {/* Search button */}
                        <button
                            className="btn btn-sm btn-primary"
                            style={{ whiteSpace: 'nowrap' }}
                            onClick={handleSearch}
                            disabled={!searchInput.trim()}
                        >
                            <i className="bx bx-search me-1"></i>Search
                        </button>
                    </div>

                    {/* No-result message shown only after a committed search */}
                    {/*{committedSearch && filteredData.length === 0 && (*/}
                    {/*    <small className="text-danger">No records found for "{committedSearch}".</small>*/}
                    {/*)}*/}
                </div>

                {/* Right: Filter + downloads */}
                <div className="col-12 col-md-5 mt-2 mt-md-0 d-flex flex-row-reverse align-items-center gap-2 flex-wrap">
                    <button className="btn btn-sm btn-primary" onClick={handleExcelDownload}>
                        <i className="bx bx-export me-sm-1"></i>
                        <span className="d-none d-sm-inline-block">Excel</span>
                    </button>
                    <button className="btn btn-sm btn-success" onClick={handleCSVDownload}>
                        <i className="bx bx-export me-sm-1"></i>
                        <span className="d-none d-sm-inline-block">CSV</span>
                    </button>

                    {/* Filter button with dropdown */}
                    <div style={{ position: 'relative' }}>
                        <button
                            type="button"
                            className={`btn btn-sm ${appliedFilterType ? "btn-success" : "btn-primary"}`}
                            onClick={() => {
                                setPendingFilterType(appliedFilterType);
                                setPendingFromDate(appliedFromDate);
                                setPendingToDate(appliedToDate);
                                setShowFilterPanel(prev => !prev);
                            }}
                        >
                            <i className="bx bx-filter-alt me-1"></i>

                            {appliedFilterType ? (
                                <>
                                    <span>{appliedFilterType}</span>

                                    <span
                                        className="badge bg-white text-success ms-2"
                                        style={{
                                            fontSize: "10px",
                                            maxWidth: "220px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        {getFilterDateDisplay()}
                                    </span>
                                </>
                            ) : (
                                "Filter"
                            )}
                        </button>
                        {showFilterPanel && filterPanel()}
                    </div>
                </div>
            </div>
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            {(loading || paginationLoading) ? (
                <TableSkeletonLoading />
            ) : !isDataLoaded ? (
                <TableSkeletonLoading />
            ) : (
                <>
                    {filterUI()}

                            {appliedFilterType && (
                                <div className="alert alert-info py-2 px-3 mt-2 mb-2">
                                    <strong>Applied Filter:</strong>{" "}
                                    {appliedFilterType}
                                    {" | "}
                                    <strong>Date Range:</strong>{" "}
                                    {getFilterDateDisplay()}
                                    {" | "}
                                    <strong>Total Records:</strong>{" "}
                                    {filteredCount ?? totalCount}
                                </div>
                            )}

                    <div className="row">
                        <div className="col-xl-12 col-lg-12 col-md-12 order-0 order-md-1">
                            <div className="card mt-2">
                                <div>
                                    {membersData && membersData.length === 0 && (
                                        <div style={{ textAlign: 'center', padding: '20px' }}>
                                            <h5 className="text-danger">There are no records to display.</h5>
                                        </div>
                                    )}

                                    {membersData.length > 0 && (
                                        <CommonTables
                                            tableHeads={tableHeads}
                                            tableData={tableElements}
                                            perPage={perPage}
                                            currentPage={currentPage}
                                            perPageChange={handlePerPageChange}
                                            pageChange={handlePageChange}
                                            totalCount={totalCount}
                                        />
                                    )}
                                </div>

                                <Snackbar
                                    open={snackbarOpen}
                                    autoHideDuration={3000}
                                    onClose={handleSnackbarClose}
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <Alert onClose={handleSnackbarClose} severity="success">
                                        {snackbarMessage}
                                    </Alert>
                                </Snackbar>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}